import { QueryClient, QueryKey } from 'react-query';

import type { SDKError } from '@eineblume/chatie-chat-sdk';
import axios from 'axios';

import { QUERY_KEYS } from '@constants';

const isAxiosNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

const getErrorMessage = (error: unknown): string =>
  error instanceof Error
    ? error.message
    : typeof error === 'string'
    ? error
    : '';

const isExactHttpNotFoundMessage = (error: unknown): boolean =>
  /^HTTP Error:\s*404(?:\s+Not Found)?$/i.test(getErrorMessage(error));

const getErrorCode = (error: unknown): string => {
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return '';
  }

  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : '';
};

export const isChatRoomDetailNotFoundError = isAxiosNotFoundError;

export const isInactiveChatRoomSDKError = (
  error: SDKError,
  currentChatRoomId: number
): boolean => {
  const isChatRoomUnavailableType =
    error.type === 'CHAT_ROOM_UNAVAILABLE';
  const hasChatRoomUnavailableCode =
    getErrorCode(error.originalError) === 'ChatRoomUnavailable';

  if (
    error.type !== 'UNKNOWN' &&
    !isChatRoomUnavailableType &&
    !hasChatRoomUnavailableCode
  ) {
    return false;
  }

  const errorChatRoomId = error.context?.chatRoomId;
  if (`${errorChatRoomId}` !== `${currentChatRoomId}`) {
    return false;
  }

  return (
    isChatRoomUnavailableType ||
    hasChatRoomUnavailableCode ||
    isAxiosNotFoundError(error.originalError) ||
    isExactHttpNotFoundMessage(error.originalError) ||
    isExactHttpNotFoundMessage(error.message)
  );
};

export type ChatRoomSDKToastMessage =
  | string
  | { text: string; height?: number };

export const showChatRoomSDKToast = (
  message: ChatRoomSDKToastMessage,
  hasHandledUnavailableChatRoom: boolean,
  showToast: (toast: { text: string; height: number }) => void
) => {
  if (hasHandledUnavailableChatRoom) {
    return;
  }

  if (typeof message === 'string') {
    showToast({ text: message, height: 100 });
    return;
  }

  showToast({ text: message.text, height: message.height ?? 100 });
};

const refreshChatRoomListQueries = (
  queryClient: QueryClient,
  queryKey: QueryKey,
  exact: boolean
) => {
  void queryClient.invalidateQueries(queryKey, {
    exact,
    active: true,
    inactive: false,
  });
  queryClient.removeQueries(queryKey, {
    exact,
    active: false,
    inactive: true,
  });
};

export const removeInactiveChatRoomQueries = (
  queryClient: QueryClient,
  chatRoomIds: number[]
) => {
  chatRoomIds.forEach((chatRoomId) => {
    queryClient.removeQueries(QUERY_KEYS.CHAT_ROOM.DETAIL(chatRoomId), {
      exact: true,
    });
    queryClient.removeQueries(QUERY_KEYS.CHAT_ROOM.PREV_ROOT(chatRoomId), {
      exact: false,
    });
    queryClient.removeQueries(
      QUERY_KEYS.CHAT_ROOM.RECOMMEND_MESSAGES(chatRoomId),
      { exact: true }
    );
    queryClient.removeQueries(
      QUERY_KEYS.CHAT_ROOM.CHEMING_STEP_STATUS(chatRoomId),
      { exact: true }
    );
    queryClient.removeQueries(QUERY_KEYS.HIDDEN_FEELING(`${chatRoomId}`), {
      exact: true,
    });
    queryClient.removeQueries(QUERY_KEYS.IMAGES.BY_CHAT_ROOM(`${chatRoomId}`), {
      exact: true,
    });
    queryClient.removeQueries(QUERY_KEYS.CHAPTER.BY_CHAT_ROOM(chatRoomId), {
      exact: false,
    });
  });

  if (chatRoomIds.length === 0) {
    return;
  }

  refreshChatRoomListQueries(
    queryClient,
    QUERY_KEYS.CHAT_ROOM.LIST_ROOT,
    false
  );
  refreshChatRoomListQueries(
    queryClient,
    QUERY_KEYS.CHAT_ROOM.STORY_LIST,
    true
  );
  refreshChatRoomListQueries(
    queryClient,
    QUERY_KEYS.CHAT_ROOM.ACTIVE_CHARACTER_ROOT,
    false
  );
  refreshChatRoomListQueries(
    queryClient,
    QUERY_KEYS.CHAT_ROOM.ACTIVE_CHARACTER_PAGINATION_ROOT,
    false
  );
};
